'use client'

import { BackdropLoading } from '@/components/backdrop-loading'
import { resetPassword } from '@/lib/api/auth/auth'
import { useAuth } from '@/store/hooks'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { MdCheckCircle, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { toast } from 'react-toastify'

export default function ResetPasswordPage() {
    const [isLoading, setIsLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [passwordReset, setPasswordReset] = useState(false)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const params = useSearchParams()

    const token = params[0].get('token')

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }

        if (!token) {
            toast.error('Token de recuperação inválido')
            navigate('/login')
        }
    }, [isAuthenticated, token])

    const passwordValidations = {
        length:
            password.length >= 8 &&
            /[a-zA-Z]/.test(password) &&
            /[0-9]/.test(password),
        uppercase: /[A-Z]/.test(password),
        special: /[@!#?.&$*]/.test(password),
        noSpaces: !password.includes(' '),
    }

    const allPasswordValidationsValid =
        Object.values(passwordValidations).every(Boolean)
    const passwordsMatch = password === confirmPassword
    const isFormValid =
        allPasswordValidationsValid &&
        passwordsMatch &&
        password.length > 0 &&
        confirmPassword.length > 0

    const handleSubmit = async () => {
        setIsLoading(true)

        if (!isFormValid) {
            toast.warning('Por favor, atenda a todos os critérios de senha.')
            setIsLoading(false)
            return
        }

        if (!token) {
            toast.error('Token inválido')
            setIsLoading(false)
            return
        }

        const response = await resetPassword(token, password)

        if (response.success) {
            setPasswordReset(true)
            toast.success('Senha redefinida com sucesso!')
        } else {
            toast.error(
                response.error ||
                    'Ocorreu um erro ao redefinir a senha. Tente novamente.'
            )
        }

        setIsLoading(false)
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    if (passwordReset) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <MdCheckCircle className="text-green-600 text-2xl" />
                        </div>

                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            Senha Redefinida!
                        </h1>

                        <p className="text-gray-600 mb-8">
                            Sua senha foi redefinida com sucesso. Agora você
                            pode fazer login com sua nova senha.
                        </p>

                        <div className="space-y-4">
                            <Link
                                to="/login"
                                className="w-full btn btn-secondary"
                            >
                                Fazer Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {isLoading && <BackdropLoading visible={isLoading} />}

            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Redefinir Senha
                            </h1>
                            <p className="text-gray-600">
                                Digite sua nova senha abaixo.
                            </p>
                        </div>

                        <section
                            data-form-id="reset-password"
                            className="space-y-6"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nova Senha
                                </label>
                                <div className="relative">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        value={password}
                                        onChange={e =>
                                            setPassword(e.target.value)
                                        }
                                        className="input input-bordered w-full pr-12"
                                        placeholder="Digite sua nova senha"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <MdVisibilityOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <MdVisibility className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>

                                {password && (
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                    passwordValidations.length
                                                        ? 'bg-green-500'
                                                        : 'bg-gray-300'
                                                }`}
                                            >
                                                {passwordValidations.length && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                A senha deve conter ao menos 8
                                                caracteres com letras e números
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                    passwordValidations.uppercase
                                                        ? 'bg-green-500'
                                                        : 'bg-gray-300'
                                                }`}
                                            >
                                                {passwordValidations.uppercase && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Ao menos 1 letra maiúscula
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                    passwordValidations.special
                                                        ? 'bg-green-500'
                                                        : 'bg-gray-300'
                                                }`}
                                            >
                                                {passwordValidations.special && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Ao menos 1 caractere especial
                                                como @! # ?. & $ *
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                    passwordValidations.noSpaces
                                                        ? 'bg-green-500'
                                                        : 'bg-gray-300'
                                                }`}
                                            >
                                                {passwordValidations.noSpaces && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Não conter espaços em branco
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirmar Nova Senha
                                </label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={e =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    className="input input-bordered w-full"
                                    placeholder="Confirme sua nova senha"
                                    required
                                />

                                {confirmPassword && (
                                    <div className="mt-3">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                    password === confirmPassword
                                                        ? 'bg-green-500'
                                                        : 'bg-red-500'
                                                }`}
                                            >
                                                {password ===
                                                confirmPassword ? (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span
                                                className={`text-sm ${
                                                    password === confirmPassword
                                                        ? 'text-green-600'
                                                        : 'text-red-600'
                                                }`}
                                            >
                                                {password === confirmPassword
                                                    ? 'As senhas coincidem'
                                                    : 'As senhas não coincidem'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                className={`w-full btn btn-secondary ${
                                    !isFormValid
                                        ? 'opacity-50 cursor-not-allowed'
                                        : ''
                                }`}
                                disabled={isLoading || !isFormValid}
                                onClick={handleSubmit}
                            >
                                {isLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Redefinir Senha'
                                )}
                            </button>
                        </section>

                        <div className="mt-6 text-center">
                            <Link
                                to="/login"
                                className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                            >
                                ← Voltar ao Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
